import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Layers, CheckCircle2 } from "lucide-react";

const isHk = (dep) => (dep || "").toLowerCase().includes("housekeep");

function DeptLine({ pr, hours, row, readOnly, setRoomCell, roomsTotal }) {
  return (
    <div className="grid grid-cols-12 items-center gap-2 text-sm" data-testid={`multi-line-${pr.key}`}>
      <div className="col-span-4 font-medium">{pr.department}</div>
      <div className="col-span-2 font-mono tabular-nums">{hours.toFixed(2)} <span className="text-[0.6rem] text-muted-foreground">hrs</span></div>
      {isHk(pr.department) ? (
        <>
          <Input className="col-span-2 h-8" inputMode="decimal" placeholder="Stay" disabled={readOnly} value={row.stayover_cleaned ?? ""} onChange={(ev) => setRoomCell(pr, "stayover_cleaned", ev.target.value)} data-testid={`multi-stayover-${pr.key}`} />
          <Input className="col-span-2 h-8" inputMode="decimal" placeholder="C/O" disabled={readOnly} value={row.checkout_cleaned ?? ""} onChange={(ev) => setRoomCell(pr, "checkout_cleaned", ev.target.value)} data-testid={`multi-checkout-${pr.key}`} />
          <div className="col-span-2 font-mono text-xs" data-testid={`multi-rooms-${pr.key}`}>{roomsTotal(row) === "" ? "—" : roomsTotal(row)} rooms</div>
        </>
      ) : <div className="col-span-6" />}
    </div>
  );
}

export const MultiDeptVerify = ({ items, payrollMap, verified, onToggle, readOnly, setRoomCell, roomsTotal }) => {
  if (!items.length) return null;
  const left = items.filter((x) => !verified.has(x.eid)).length;
  return (
    <div id="multi-dept-verify" className={`mb-4 border p-4 ${left ? "border-amber-400/70 bg-amber-50/60 dark:bg-amber-950/20" : "border-emerald-400/60 bg-emerald-50/50 dark:bg-emerald-950/20"}`} data-testid="multi-dept-verify">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-bold"><Layers className="h-4 w-4" /> Worked in 2+ departments today ({items.length})</h3>
        <span className={`text-xs font-semibold ${left ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-400"}`} data-testid="multi-dept-left">
          {left ? `${left} to verify before saving` : <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> All verified</span>}
        </span>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">Hours come from each department's Time Clock punches. Check the split and enter Housekeeping rooms, then tick Verified.</p>
      <div className="grid gap-3 lg:grid-cols-2">
        {items.map((x) => (
          <div key={x.eid} className="rounded-md border border-border bg-card p-3" data-testid={`multi-card-${x.eid}`}>
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="font-semibold">{x.name}</span>
              <span className={`font-mono text-xs ${x.clocked && Math.abs(x.total - x.clocked) > 0.01 ? "font-bold text-red-600" : "text-muted-foreground"}`} data-testid={`multi-total-${x.eid}`}>
                {x.total.toFixed(2)} hrs total{x.clocked ? ` · clocked ${x.clocked.toFixed(2)}` : ""}
              </span>
            </div>
            <div className="space-y-1.5">
              {x.rows.map((r) => (
                <DeptLine key={r.pr.key} pr={r.pr} hours={r.hours} row={payrollMap[r.pr.key] || {}} readOnly={readOnly} setRoomCell={setRoomCell} roomsTotal={roomsTotal} />
              ))}
            </div>
            <label className="mt-3 flex items-center gap-2 border-t border-border pt-2 text-sm font-medium" data-testid={`multi-verify-label-${x.eid}`}>
              <Checkbox checked={verified.has(x.eid)} disabled={readOnly} onCheckedChange={() => onToggle(x.eid)} data-testid={`multi-verify-${x.eid}`} />
              Verified
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};
